<template>
    <section class="py-16 lg:py-[150px] overflow-hidden relative" :class="backgroundColor">
        <img v-if="backgroundImage" class="z-0 absolute left-0 top-2 hidden lg:block w-full" :src="$asset(backgroundImage, 'public')" alt="" />

        <div class="mx-auto">
            <h2 class="text-[28px] md:text-4xl lg:text-[44px] lg:leading-[57px] font-semibold mx-auto mb-[50px] text-center" :class="titleColor">
                <span v-html="renderTitle()"></span>
            </h2>
            <div ref="cardsSection" class="overflow-x-hidden pb-4 mb-[50px] cardsSection">
                <div ref="cardsContainer" class="flex gap-5 min-w-max px-0 cardsSection" :style="{ transform: `translateX(${cardsTranslateX}px)` }">
                    <div v-for="(card, index) in cards" :key="index" class="p-6 lg:p-[30px] rounded-2xl flex-shrink-0" :class="[cardBackgroundColor, cardWidth]">
                        <h3 class="text-[24px] font-semibold mb-4 text-center" :class="cardTitleColor" v-html="card.title"></h3>
                        <div class="mx-auto mb-4 flex items-center justify-center">
                            <img
                                v-if="card.icon && (card.icon.startsWith('/') || card.icon.startsWith('http'))"
                                :src="$asset(card.icon, 'public')"
                                class="mx-auto block"
                                :class="iconSize"
                                :alt="card.title"
                            />
                            <i v-else class="text-[#006CFF]" :class="[card.icon, iconSize]"></i>
                        </div>
                        <p class="text-center font-pro text-[18px]" :class="cardTextColor">
                            {{ card.content }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="text-center w-full px-5 lg:px-[0]">
                <router-link
                    v-if="showCta"
                    :to="ctaLink"
                    class="flex justify-center lg:justify-normal lg:inline-flex items-center gap-2 text-white lg:px-4 py-4 rounded-full font-semibold transition hover:opacity-90"
                    :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                >
                    {{ ctaText }}
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </router-link>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    backgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    backgroundImage: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    cards: {
        type: Array,
        default: () => []
    },
    cardBackgroundColor: {
        type: String,
        default: 'bg-blue-light'
    },
    cardTitleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    cardTextColor: {
        type: String,
        default: 'text-black'
    },
    cardIconColor: {
        type: String,
        default: 'text-[#006CFF]'
    },
    iconSize: {
        type: String,
        default: 'text-[80px]'
    },
    cardWidth: {
        type: String,
        default: 'lg:w-[25rem] w-80'
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    buttonGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    buttonGradientTo: {
        type: String,
        default: '#0C7475'
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    }
});

const cardsSection = ref(null);
const cardsContainer = ref(null);
const cardsTranslateX = ref(0);

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) {
                return part;
            }

            return `<span class="bg-gradient-to-r text-transparent bg-clip-text" style="background: linear-gradient(to right, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};

const handleParallaxScroll = () => {
    const section = cardsSection.value;
    const container = cardsContainer.value;

    if (!section || !container) return;

    const rect = section.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const sectionTop = rect.top;
    const sectionHeight = rect.height;

    const scrollStart = windowHeight;
    const scrollEnd = -sectionHeight;
    const scrollRange = scrollStart - scrollEnd;

    let progress = (scrollStart - sectionTop) / scrollRange;
    progress = Math.max(0, Math.min(1, progress));

    const containerWidth = container.scrollWidth;
    const viewportWidth = section.clientWidth;
    const maxTranslation = containerWidth - viewportWidth;

    const startOffset = 2500;
    cardsTranslateX.value = startOffset - (progress + 0.4) * (maxTranslation + startOffset);
};

onMounted(() => {
    window.addEventListener('scroll', handleParallaxScroll);
    handleParallaxScroll();
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleParallaxScroll);
});
</script>

<style scoped>
.cardsSection {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.cardsSection::-webkit-scrollbar {
    display: none;
}
</style>
